import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useForm from './useForm';
import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.webpack';
import './style.css';
import Modal from '../../../components/UI/modal/Modal';
import { CircularProgress } from '@material-ui/core';
import Avatar from 'react-avatar';
import Text from '../../../components/UI/Text/Text';
import { useHistory, Link } from 'react-router-dom';
import { auth } from '../../../firebase-init';
import { canDownloadBooks, downloadBookPdf } from '../../../helpers/bookDownload';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const ViewBook = (props) => {
  let history = useHistory();
  const {
    backToDetails,
    title,
    book,
    bookFile,
    numPages,
    pageNumber,
    onDocumentLoadSuccess,
    previousPage,
    nextPage,
    goPage,
    newPageNumber,
    setNewPageNumber,
    jumpToPage,
    showModal,
    setShowModal,
    loading,
    error,
    setError,
  } = useForm(props);
  const { t } = useTranslation();
  const { user } = props;
  const [downloading, setDownloading] = useState(false);
  const allowDownload = canDownloadBooks(user);

  const downloadBook = async () => {
    if (!book?.id || downloading) return;
    setDownloading(true);
    try {
      await downloadBookPdf(book.id || props.match.params.id, title || book.title);
    } catch (err) {
      console.error('Book download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const goToProfile = () => {
    history.push('/profile');
  };

  const logout = () => {
    auth.signOut();
  };

  const stageRef = useRef(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [stageSize, setStageSize] = useState({ w: 900, h: 700 });
  const [pageAspect, setPageAspect] = useState(0.72);
  const [fitMode, setFitMode] = useState('page');
  const [zoom, setZoom] = useState(1);
  const [pinchStartDistance, setPinchStartDistance] = useState(null);
  const [pinchStartZoom, setPinchStartZoom] = useState(1);

  const minSwipeDistance = 50;
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.1;

  const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));
  const zoomIn = () => {
    setFitMode('custom');
    setZoom((z) => clampZoom(z + ZOOM_STEP));
  };
  const zoomOut = () => {
    setFitMode('custom');
    setZoom((z) => clampZoom(z - ZOOM_STEP));
  };
  const fitPage = () => {
    setFitMode('page');
    setZoom(1);
  };
  const fitWidth = () => {
    setFitMode('width');
    setZoom(1);
  };

  const getTouchDistance = (touches) => {
    if (!touches || touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  };

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      setStageSize({
        w: el.clientWidth || window.innerWidth,
        h: el.clientHeight || window.innerHeight - 140,
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      if (e.key === 'ArrowLeft' && pageNumber > 1) {
        e.preventDefault();
        previousPage();
      } else if (e.key === 'ArrowRight' && pageNumber < numPages) {
        e.preventDefault();
        nextPage();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        zoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        fitPage();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [pageNumber, numPages, previousPage, nextPage]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setFitMode('custom');
      setZoom((z) => clampZoom(z + delta));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onTouchStart = (e) => {
    if (e.targetTouches.length === 2) {
      setPinchStartDistance(getTouchDistance(e.targetTouches));
      setPinchStartZoom(zoom);
      setTouchStart(0);
      setTouchEnd(0);
      return;
    }
    setPinchStartDistance(null);
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    if (e.targetTouches.length === 2 && pinchStartDistance) {
      e.preventDefault();
      const distance = getTouchDistance(e.targetTouches);
      if (distance > 0) {
        setFitMode('custom');
        setZoom(clampZoom(pinchStartZoom * (distance / pinchStartDistance)));
      }
      return;
    }
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (pinchStartDistance) {
      setPinchStartDistance(null);
      return;
    }
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance && pageNumber < numPages) nextPage();
    if (distance < -minSwipeDistance && pageNumber > 1) previousPage();
  };

  const onPageLoadSuccess = useCallback((page) => {
    if (page?.width && page?.height) {
      setPageAspect(page.width / page.height);
    }
  }, []);

  const gutter = 96;
  const availableW = Math.max(240, stageSize.w - gutter);
  const availableH = Math.max(240, stageSize.h - 28);
  const fitHeightW = availableH * pageAspect;
  const fitBase = fitMode === 'width' ? availableW : Math.min(availableW, fitHeightW);
  const pageWidth = Math.round(fitBase * zoom);

  const onDocumentLoadError = (loadError) => {
    console.log('error :', loadError);
    setError('ERROR_LOADING_PDF');
  };

  const totalPages = numPages || book.pages || 1;
  const canPrev = pageNumber > 1;
  const canNext = pageNumber < totalPages;
  const progress = totalPages > 1 ? ((pageNumber - 1) / (totalPages - 1)) * 100 : 0;

  const body = () => {
    const isUFCStudent =
      props.user?.student?.establishment?.name &&
      (props.user.student.establishment.name.includes('U.F.C') ||
        props.user.student.establishment.name.includes('Université de la Formation Continue') ||
        props.user.student.establishment.name.includes('Formation Continue') ||
        props.user.student.establishment.name.includes('UFC'));

    const FREE_FULL_PUBLICATION_NUMBERS = [6665, 6666, 6667, 6668];
    const isFreeFullBook = FREE_FULL_PUBLICATION_NUMBERS.includes(Number(book?.publicationNumber));
    const hasFullAccess =
      isFreeFullBook ||
      props.user?.student?.subscriptionStatus === 'verified_and_paid' ||
      props.user?.student?.subscriptionStatus === 'verified_access' ||
      props.user?.student?.hasFullLibraryAccess === true;

    return (
      <div className="modal-body-inactivated-account">
        <div className="modal-inactivated-account-container">
          <img className="modal-inactivated-account-img" src="assets/images/not-activated.png" alt="not-activated" />
          {props.user === null ? (
            <div className="modal-inactivated-account-text">{t('user-not-authenticated')}</div>
          ) : !props.user.isActivated ? (
            <div className="modal-inactivated-account-text">{t('account-not-activated')}</div>
          ) : hasFullAccess ? null : !props.user.isEstablishmentActivated && !isUFCStudent ? (
            <div className="modal-inactivated-account-text">{t('establishment-not-activated')}</div>
          ) : (
            <div className="modal-inactivated-account-text">{t('account-not-activated')}</div>
          )}
        </div>
        <div className="modal-inactivated-account-exit-button-container">
          <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
            {t('annuler-modal-inactivated')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="book-reader">
      <header className="reader-topbar">
        <button type="button" className="reader-back" onClick={backToDetails}>
          <span className="reader-back-icon" aria-hidden="true">
            ‹
          </span>
          <span className="reader-back-label">{t('book-details')}</span>
        </button>
        <div className="reader-brand">
          <h1 className="reader-title" title={title}>
            {title}
          </h1>
        </div>
        <div className="reader-user">
          {allowDownload ? (
            <button type="button" className="reader-download" onClick={downloadBook} disabled={downloading}>
              {downloading ? t('books.details.downloading') : t('books.details.download')}
            </button>
          ) : null}
          {user !== null ? (
            <div className="dropdown view-book-profile">
              <a
                className="dropdown-toggle profile-header-dropdown reader-profile"
                href="#"
                role="button"
                id="dropdownMenuLink"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <Avatar name={user.name} size="36" round={true} />
                <span className="view-book-profile-name">
                  {user && <Text text={user.name} maxLength={18} />}
                </span>
              </a>
              <ul className="dropdown-menu p-2 w-100" aria-labelledby="dropdownMenuLink">
                <li className="dropdown-item" onClick={goToProfile}>
                  {t('header.update-profile')}
                </li>
                <li className="dropdown-item" onClick={logout}>
                  {t('header.logout')}
                </li>
              </ul>
            </div>
          ) : (
            <Link to="/login" className="reader-login">
              {t('login.form.connect')}
            </Link>
          )}
        </div>
      </header>

      <div
        className={`reader-stage ${zoom > 1.02 || fitMode === 'custom' ? 'is-zoomed' : ''}`}
        ref={stageRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button
          type="button"
          className="reader-nav reader-nav-prev"
          onClick={previousPage}
          disabled={!canPrev}
          aria-label="Previous page"
        >
          ‹
        </button>
        <div className="pdf-viewer-wrapper">
          {loading === true ? (
            <CircularProgress color="inherit" className="reader-spinner" />
          ) : error !== null ? (
            <h3 className="text-danger">{t(error)}</h3>
          ) : (
            <Document
              className="pdf-document"
              file={bookFile}
              onLoadError={onDocumentLoadError}
              onLoadSuccess={onDocumentLoadSuccess}
            >
              <Page pageNumber={pageNumber} width={pageWidth} onLoadSuccess={onPageLoadSuccess} />
            </Document>
          )}
        </div>
        <button
          type="button"
          className="reader-nav reader-nav-next"
          onClick={nextPage}
          disabled={!canNext}
          aria-label="Next page"
        >
          ›
        </button>
      </div>

      <form className="reader-toolbar" onSubmit={goPage}>
        <div className="reader-toolbar-group reader-pages">
          <button type="button" className="reader-tool-btn" onClick={previousPage} disabled={!canPrev}>
            ‹
          </button>
          <label className="reader-page-jump">
            <span>
              {t('page')} {pageNumber} {t('of')} {totalPages}
            </span>
            <input
              className="number-input"
              type="number"
              value={newPageNumber}
              onChange={(e) => setNewPageNumber(e.target.value)}
              min={1}
              max={totalPages}
              aria-label={t('jump-to')}
            />
            <button type="submit" className="go-button">
              {t('go')}
            </button>
          </label>
          <button type="button" className="reader-tool-btn" onClick={nextPage} disabled={!canNext}>
            ›
          </button>
        </div>
        <input
          className="reader-progress"
          type="range"
          min={1}
          max={totalPages}
          value={pageNumber}
          onChange={(e) => jumpToPage(e.target.value)}
          style={{ '--progress': `${progress}%` }}
          aria-label={t('page')}
        />
        <div className="reader-toolbar-group reader-zoom">
          <button type="button" className="reader-tool-btn" onClick={zoomOut} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">
            −
          </button>
          <button type="button" className="reader-zoom-level" onClick={fitPage} title="Fit page">
            {Math.round(zoom * 100)}%
          </button>
          <button type="button" className="reader-tool-btn" onClick={zoomIn} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">
            +
          </button>
          <button type="button" className={`reader-fit ${fitMode === 'page' ? 'is-active' : ''}`} onClick={fitPage}>
            {t('page')}
          </button>
          <button type="button" className={`reader-fit ${fitMode === 'width' ? 'is-active' : ''}`} onClick={fitWidth}>
            ↔
          </button>
        </div>
        <Modal open={showModal} setOpen={setShowModal} body={body()} className="book-view-alert-no-activated" />
      </form>
    </div>
  );
};

export default ViewBook;
